import { SeawardException } from '@ch-types/SeawardExceptions';
import { IArgumentType } from '@ch-types/Exception';

/**
 * Base exception for all SXClient-related errors.
 * Extends SeawardException for cross-frame serialization support.
 */
export class SXClientException extends SeawardException {
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'SXClientException';
    }

    public deserialize(obj): SXClientException {
        return new SXClientException({ message: obj.message });
    }
}

/**
 * Base exception for SXClient printer errors
 */
export class SXClientPrinterException extends SXClientException {
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'SXClientPrinterException';
    }

    public deserialize(obj): SXClientPrinterException {
        return new SXClientPrinterException({ message: obj.message });
    }
}

/**
 * Thrown when print job fails
 */
export class PrintFailureException extends SXClientPrinterException {
    constructor(message?: string) {
        super({ message: message || 'Print job failed' });
        this.model = 'PrintFailureException';
    }

    public deserialize(obj): PrintFailureException {
        return new PrintFailureException(obj.message);
    }
}

/**
 * Thrown when print configuration is invalid
 */
export class InvalidPrintConfigException extends SXClientPrinterException {
    constructor(message?: string) {
        super({ message: message || 'Invalid print configuration' });
        this.model = 'InvalidPrintConfigException';
    }

    public deserialize(obj): InvalidPrintConfigException {
        return new InvalidPrintConfigException(obj.message);
    }
}

/**
 * Base exception for SXClient scanner errors
 */
export class SXClientScannerException extends SXClientException {
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'SXClientScannerException';
    }

    public deserialize(obj): SXClientScannerException {
        return new SXClientScannerException({ message: obj.message });
    }
}

/**
 * Thrown when scanner is not initialized before use
 */
export class ScannerNotInitializedException extends SXClientScannerException {
    constructor() {
        super({ message: 'Scanner must be initialized before use' });
        this.model = 'ScannerNotInitializedException';
    }

    public deserialize(obj): ScannerNotInitializedException {
        return new ScannerNotInitializedException();
    }
}

/**
 * Thrown when scanner is not available or not connected
 */
export class ScannerNotAvailableException extends SXClientScannerException {
    constructor() {
        super({ message: 'Scanner is not available' });
        this.model = 'ScannerNotAvailableException';
    }

    public deserialize(obj): ScannerNotAvailableException {
        return new ScannerNotAvailableException();
    }
}

/**
 * Thrown when scan operation fails
 */
export class ScanFailureException extends SXClientScannerException {
    constructor(message?: string) {
        super({ message: message || 'Scan operation failed' });
        this.model = 'ScanFailureException';
    }

    public deserialize(obj): ScanFailureException {
        return new ScanFailureException(obj.message);
    }
}

/**
 * Maps SXClient errors to appropriate exception types
 */
export class SXClientErrorMapper {
    public static mapPrinterError(error: any): Error {
        const errorMessage = error?.message || error?.toString() || 'Unknown printer error';
        return new PrintFailureException(errorMessage);
    }

    public static mapScannerError(error: any): SXClientScannerException {
        const errorMessage = error?.message || error?.toString() || 'Unknown scanner error';
        return new ScanFailureException(errorMessage);
    }
}
